import { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { useAuth0 } from '@auth0/auth0-react';
import { fetchWithAuth } from '../../lib/api/fetchWithAuth';
import { toast } from 'react-hot-toast';

interface AddCreatorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreatorAdded: () => Promise<void>;
  brandId: string;
  orgId: string;
}

export function AddCreatorModal({ 
  isOpen, 
  onClose, 
  onCreatorAdded,
  brandId,
  orgId
}: AddCreatorModalProps) {
  const { getAccessTokenSilently } = useAuth0();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: '',
    timezone: '',
    retainerAmount: '500',
    commission: '25',
    postsPerMonth: '30',
    tiktokHandle: '',
    instagramHandle: '',
    youtubeHandle: '',
    bankFirstName: '',
    bankLastName: '',
    bankAccountNumber: '',
    bankRoutingNumber: '',
    bankAccountType: '',
    venmoHandle: '',
    zelleEmail: '',
    zellePhone: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Validate numeric fields
      const retainerAmount = Number.parseInt(formData.retainerAmount, 10);
      const commission = Number.parseInt(formData.commission, 10);
      const postsPerMonth = Number.parseInt(formData.postsPerMonth, 10);

      if (Number.isNaN(retainerAmount) || retainerAmount < 0) {
        toast.error('Please enter a valid retainer amount');
        return;
      }

      if (Number.isNaN(commission) || commission < 0 || commission > 100) {
        toast.error('Please enter a valid commission percentage (0-100)');
        return;
      }

      if (Number.isNaN(postsPerMonth) || postsPerMonth < 0) {
        toast.error('Please enter a valid number of posts per month');
        return;
      }

      const token = await getAccessTokenSilently();
      await fetchWithAuth(`/api/organizations/${orgId}/brands/${brandId}/creators`, {
        method: 'POST',
        token,
        body: JSON.stringify({
          ...formData,
          retainerAmount,
          commission,
          postsPerMonth
        })
      });

      toast.success('Creator added successfully');
      await onCreatorAdded();
      onClose();
    } catch (error) {
      console.error('Failed to add creator:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to add creator');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      className="fixed inset-0 z-50 overflow-y-auto"
    >
      <div className="flex items-center justify-center min-h-screen">
        <div className="fixed inset-0 bg-black opacity-30" />

        <div className="relative bg-gray-800 rounded-lg p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
          <Dialog.Title className="text-xl font-bold text-gray-100 mb-6">
            Add New Creator
          </Dialog.Title>

          <form onSubmit={handleSubmit} className="space-y-6 h-[calc(90vh-8rem)] overflow-y-auto pr-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-300 mb-1">
                  First Name *
                </label>
                <input
                  id="firstName"
                  type="text"
                  name="firstName"
                  required
                  value={formData.firstName}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-300 mb-1">
                  Last Name
                </label>
                <input
                  id="lastName"
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
                  Email *
                </label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-1">
                  Phone
                </label>
                <input
                  id="phone"
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div className="col-span-2">
                <label htmlFor="addressLine1" className="block text-sm font-medium text-gray-300 mb-1">
                  Address Line 1 *
                </label>
                <input
                  id="addressLine1"
                  type="text"
                  name="addressLine1"
                  required
                  value={formData.addressLine1}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div className="col-span-2">
                <label htmlFor="addressLine2" className="block text-sm font-medium text-gray-300 mb-1">
                  Address Line 2
                </label>
                <input
                  id="addressLine2"
                  type="text"
                  name="addressLine2"
                  value={formData.addressLine2}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label htmlFor="city" className="block text-sm font-medium text-gray-300 mb-1">
                  City *
                </label>
                <input
                  id="city"
                  type="text"
                  name="city"
                  required
                  value={formData.city}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label htmlFor="state" className="block text-sm font-medium text-gray-300 mb-1">
                  State *
                </label>
                <input
                  id="state"
                  type="text"
                  name="state"
                  required
                  value={formData.state}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label htmlFor="zipCode" className="block text-sm font-medium text-gray-300 mb-1">
                  ZIP Code *
                </label>
                <input
                  id="zipCode"
                  type="text"
                  name="zipCode"
                  required
                  value={formData.zipCode}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label htmlFor="timezone" className="block text-sm font-medium text-gray-300 mb-1">
                  Timezone
                </label>
                <input
                  id="timezone"
                  type="text"
                  name="timezone"
                  value={formData.timezone}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label htmlFor="retainerAmount" className="block text-sm font-medium text-gray-300 mb-1">
                  Retainer Amount *
                </label>
                <input
                  id="retainerAmount"
                  type="text"
                  name="retainerAmount"
                  required
                  pattern="[0-9]*"
                  value={formData.retainerAmount}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label htmlFor="commission" className="block text-sm font-medium text-gray-300 mb-1">
                  Commission % *
                </label>
                <input
                  id="commission"
                  type="text"
                  name="commission"
                  required
                  pattern="[0-9]*"
                  value={formData.commission}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label htmlFor="postsPerMonth" className="block text-sm font-medium text-gray-300 mb-1">
                  Posts per Month *
                </label>
                <input
                  id="postsPerMonth"
                  type="text"
                  name="postsPerMonth"
                  required
                  pattern="[0-9]*"
                  value={formData.postsPerMonth}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label htmlFor="tiktokHandle" className="block text-sm font-medium text-gray-300 mb-1">
                  TikTok Handle
                </label>
                <input
                  id="tiktokHandle"
                  type="text"
                  name="tiktokHandle"
                  value={formData.tiktokHandle}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label htmlFor="instagramHandle" className="block text-sm font-medium text-gray-300 mb-1">
                  Instagram Handle
                </label>
                <input
                  id="instagramHandle"
                  type="text"
                  name="instagramHandle"
                  value={formData.instagramHandle}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label htmlFor="youtubeHandle" className="block text-sm font-medium text-gray-300 mb-1">
                  YouTube Handle
                </label>
                <input
                  id="youtubeHandle"
                  type="text"
                  name="youtubeHandle"
                  value={formData.youtubeHandle}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            {/* Bank Account Information */}
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-200">Bank Account Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="bankFirstName" className="block text-sm font-medium text-gray-300">
                    Bank Account First Name
                  </label>
                  <input
                    type="text"
                    id="bankFirstName"
                    name="bankFirstName"
                    value={formData.bankFirstName}
                    onChange={handleChange}
                    className="mt-1 block w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label htmlFor="bankLastName" className="block text-sm font-medium text-gray-300">
                    Bank Account Last Name
                  </label>
                  <input
                    type="text"
                    id="bankLastName"
                    name="bankLastName"
                    value={formData.bankLastName}
                    onChange={handleChange}
                    className="mt-1 block w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label htmlFor="bankAccountNumber" className="block text-sm font-medium text-gray-300">
                    Bank Account Number
                  </label>
                  <input
                    type="text"
                    id="bankAccountNumber"
                    name="bankAccountNumber"
                    value={formData.bankAccountNumber}
                    onChange={handleChange}
                    className="mt-1 block w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label htmlFor="bankRoutingNumber" className="block text-sm font-medium text-gray-300">
                    Bank Routing Number
                  </label>
                  <input
                    type="text"
                    id="bankRoutingNumber"
                    name="bankRoutingNumber"
                    value={formData.bankRoutingNumber}
                    onChange={handleChange}
                    className="mt-1 block w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label htmlFor="bankAccountType" className="block text-sm font-medium text-gray-300">
                    Bank Account Type
                  </label>
                  <select
                    id="bankAccountType"
                    name="bankAccountType"
                    value={formData.bankAccountType}
                    onChange={handleChange}
                    className="mt-1 block w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Select account type</option>
                    <option value="checking">Checking</option>
                    <option value="savings">Savings</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Alternative Payment Methods */}
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-200">Alternative Payment Methods</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="venmoHandle" className="block text-sm font-medium text-gray-300">
                    Venmo Handle
                  </label>
                  <input
                    type="text"
                    id="venmoHandle"
                    name="venmoHandle"
                    value={formData.venmoHandle}
                    onChange={handleChange}
                    className="mt-1 block w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label htmlFor="zelleEmail" className="block text-sm font-medium text-gray-300">
                    Zelle Email
                  </label>
                  <input
                    type="email"
                    id="zelleEmail"
                    name="zelleEmail"
                    value={formData.zelleEmail}
                    onChange={handleChange}
                    className="mt-1 block w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label htmlFor="zellePhone" className="block text-sm font-medium text-gray-300">
                    Zelle Phone
                  </label>
                  <input
                    type="tel"
                    id="zellePhone"
                    name="zellePhone"
                    value={formData.zellePhone}
                    onChange={handleChange}
                    className="mt-1 block w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-4 mt-8">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-300 hover:text-gray-100"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Adding...' : 'Add Creator'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Dialog>
  );
} 